import { NgModule } from "@angular/core";
import { CommonModule } from "@angular/common";
import { BrowserModule } from "@angular/platform-browser";
import { Routes, RouterModule } from "@angular/router";

import { HomeComponent } from "./home/home.component";
import { CounterComponent } from "./counter/counter.component";
import { AuthorizationGuard } from "./services/authorization.guard";
import { RegistrationGuard } from "./services/registration.guard";
import { FetchDataComponent } from "./fetch-data/fetch-data.component";
import { RegisterComponent } from "./register/register.component";
import { DashboardComponent } from "./dashboard/dashboard.component";
import { ResetPasswordComponent } from "./reset-password/reset-password.component";
import { ListCartComponent } from "./cart/list-cart/list-cart.component";
import { ListTransactionComponent } from "./transaction/list-transaction/list-transaction.component";
import { SelectPaymentComponent } from "./transaction/select-payment/select-payment.component";
import { PaymentProofComponent } from "./transaction/payment-proof/payment-proof.component";
import { ListProductComponent } from "./product/list-product/list-product.component";
import { DetailProductComponent } from "./product/detail-product/detail-product.component";
import { CheckoutComponent } from "./cart/checkout/checkout.component";
import { CheckoutGtComponent } from "./cart/checkout-gt/checkout-gt.component";
import { ListNotificationComponent } from "./notification/list-notification/list-notification.component";
import { ListPrincipalComponent } from "./principal/list-principal/list-principal.component";
import { ListAddressComponent } from "./address/list-address/list-address.component";
import { AddProductComponent } from "./product/add-product/add-product.component";
import { ForgotPasswordComponent } from "./forgot-password/forgot-password.component";
import { ChangePasswordComponent } from "./change-password/change-password.component";
import { SubmitActivationComponent } from "./activation/submit-activation/submit-activation.component";
import { WaitActivationComponent } from "./activation/wait-activation/wait-activation.component";
import { HelpComponent } from "./help/help.component";
import { SetPinLimitComponent } from "./pin-limit/set-pin-limit/set-pin-limit.component";
import { ListPromoComponent } from "./promo/list-promo/list-promo.component";
import { DetailPromoComponent } from "./promo/detail-promo/detail-promo.component";
import { ChangeLimitComponent } from "./pin-limit/change-limit/change-limit.component";
import { ChangePinComponent } from "./pin-limit/change-pin/change-pin.component";
import { ListReceivableComponent } from "./receivable/list-receivable/list-receivable.component";
import { DownloadIosComponent } from "./download-ios/download-ios.component";
import { PaymentChannelComponent } from "./receivable/payment-channel/payment-channel.component";
import { OtherPaymentChannelComponent } from "./receivable/other-payment-channel/other-payment-channel.component";
import { ProcessPaymentComponent } from "./receivable/process-payment/process-payment.component";
import { DetailPaymentComponent } from "./receivable/detail-payment/detail-payment.component";
import { ListVoucherComponent } from "./point/list-voucher/list-voucher.component";
import { DetailVoucherComponent } from "./point/detail-voucher/detail-voucher.component";
import { HistoryPointComponent } from "./point/history-point/history-point.component";
import { RewardComponent } from "./point/reward/reward.component";
import { ListProgramComponent } from "./program/list-program/list-program.component";
import { DetailProgramComponent } from "./program/detail-program/detail-program.component";
import { TrackingB2bComponent } from "./ezrx-b2b/tracking-b2b/tracking-b2b.component";
import { StockSimulationComponent } from "./ezrx-b2b/stock-simulation/stock-simulation.component";
import { DetailArtikelComponent } from "./detail-artikel/detail-artikel.component";
import { ArticleComponent } from "./article/article.component";
import { ListReturComponent } from "./receivable/list-retur/list-retur.component";
import { ListProposalComponent } from './proposal/list-proposal/list-proposal.component';
import { DetailProposalComponent } from './proposal/detail-proposal/detail-proposal.component';

import { PaymentChannelsResolver } from './resolvers/payment-channels.resolver';
import { PaymentGuideResolver } from './resolvers/payment-guide.resolver';
import { PopupMessagePaymentResolver } from './resolvers/popup-message-payment.resolver';
import { PopupMessagePaymentProofResolver } from './resolvers/popup-message-payment-proof.resolver';
import { PopupMessageCancelTransactionResolver } from './resolvers/popup-message-cancel-transaction.resolver';
import { PopupMessageReminderTransactionResolver } from './resolvers/popup-message-reminder-transaction.resolver';
import { PopupMessageRegistrationResolver } from './resolvers/popup-message-registration.resolver';
import { PopupMessageRegistrationNPWPResolver } from './resolvers/popup-message-registration-npwp.resolver';
import { PopupMessageRegistrationSuccessResolver } from './resolvers/popup-message-registration-success.resolver';
import { RegistrationResolver } from './resolvers/registration.resolver';

const routes: Routes = [
  { path: "", component: HomeComponent, pathMatch: "full" },
  { path: "home", component: HomeComponent, pathMatch: "full" },
  { path: "terms", component: HomeComponent, pathMatch: "full" },

  // secured route
  {
    path: "",
    canActivate: [AuthorizationGuard],
    canActivateChild: [AuthorizationGuard],
    children: [
      {
        path: "counter",
        component: CounterComponent,
      },
      {
        path: "fetch-data",
        component: FetchDataComponent,
      },

      {
        path: "dashboard",
        component: DashboardComponent,
      },
      {
        path: "cart",
        component: ListCartComponent,
      },
      {
        path: "transaction",
        component: ListTransactionComponent,
        resolve: {
          popup_message_cancel_transaction: PopupMessageCancelTransactionResolver,
          popup_message_reminder_transaction: PopupMessageReminderTransactionResolver
        },
      },
      {
        path: "transaction/:id/select-payment",
        component: SelectPaymentComponent,
        resolve: {
          payment_channels: PaymentChannelsResolver,
          popup_message_payment: PopupMessagePaymentResolver
        },
      },
      {
        path: "transaction/:id/payment-proof",
        component: PaymentProofComponent,
        resolve: {
          payment_guide: PaymentGuideResolver,
          popup_message_payment_proof: PopupMessagePaymentProofResolver
        }
      },
      {
        path: "product",
        component: ListProductComponent,
      },
      {
        path: "product/add",
        component: AddProductComponent,
      },
      {
        path: "product/principal/:data",
        component: ListProductComponent,
      },
      {
        path: "detail-product/:data",
        component: DetailProductComponent,
      },
      {
        path: "checkout",
        component: CheckoutComponent,
      },
      {
        path: "account/change-password",
        component: ChangePasswordComponent,
      },
      {
        path: "notification",
        component: ListNotificationComponent,
      },
      {
        path: "principal",
        component: ListPrincipalComponent,
      },
      {
        path: "address",
        component: ListAddressComponent,
      },
      {
        path: "promo",
        component: ListPromoComponent,
      },
      {
        path: "detail-promo/:id",
        component: DetailPromoComponent,
      },
      {
        path: "security/change-limit",
        component: ChangeLimitComponent,
      },
      {
        path: "security/change-pin",
        component: ChangePinComponent,
      },
      {
        path: "receivable",
        component: ListReceivableComponent,
      },
      {
        path: "receivable/payment-channel/:data",
        component: PaymentChannelComponent,
      },
      {
        path: "receivable/other-payment-channel/:data/:channel",
        component: OtherPaymentChannelComponent,
      },
      {
        path: "receivable/process-payment/:data",
        component: ProcessPaymentComponent,
      },
      {
        path: "receivable/detail-payment/:data",
        component: DetailPaymentComponent,
      },
      {
        path: "voucher",
        component: ListVoucherComponent,
      },
      {
        path: "detail-voucher/:data",
        component: DetailVoucherComponent,
      },
      {
        path: "point",
        component: HistoryPointComponent,
      },
      {
        path: "reward",
        component: RewardComponent,
      },
      {
        path: "program",
        component: ListProgramComponent,
      },
      {
        path: "program/detail/:id",
        component: DetailProgramComponent,
      },
      {
        path: "tracking-b2b",
        component: TrackingB2bComponent,
      },
      {
        path: "stock-simulation",
        component: StockSimulationComponent,
      },
      {
        path: "artikel",
        component: ArticleComponent,
      },
      {
        path: "artikel/tags/:tag",
        component: ArticleComponent
      },
      {
        path: "detail-artikel/:id",
        component: DetailArtikelComponent,
      },
      {
        path: "retur",
        component: ListReturComponent,
      },
      {
        path: "proposal",
        component: ListProposalComponent,
      },
      {
        path: "proposal/detail/:id",
        component: DetailProposalComponent,
      },
    ]
  },
  {
    path: "register",
    component: RegisterComponent,
    resolve: {
      token: RegistrationResolver,
      popup_message_registration: PopupMessageRegistrationResolver,
      popup_message_registration_npwp: PopupMessageRegistrationNPWPResolver,
      popup_message_registration_success: PopupMessageRegistrationSuccessResolver
    },
    canActivate: [RegistrationGuard],
  },
  { path: "account/forgotpassword", component: ForgotPasswordComponent },
  { path: "account/forgotpassword/:uniqueId", component: ResetPasswordComponent },
  { path: "account/submit-activation", component: SubmitActivationComponent },
  { path: "account/wait-activation", component: WaitActivationComponent },
  { path: "help", component: HelpComponent },
  { path: "security/set-pin-limit", component: SetPinLimitComponent },
  { path: "download-ios", component: DownloadIosComponent, pathMatch: "full" },
  {
    path: "**",
    redirectTo: "",
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule { }
